import { useState } from "react"
import { consultarPlacaApiBrasil } from "@/lib/consultaPlacaService"
import { validarPlaca } from "@/lib/fipeService"
import { normalizarPlaca } from "@/lib/veiculoUtils"
import { Search, Car, AlertCircle, CheckCircle, Save } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { PageHeader } from "@/components/PageHeader"
import { useIsMobile } from "@/hooks/use-mobile"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import {
  getPlateLookupUserError,
  PLACA_INVALIDA_ERROR,
  type VehicleLookupUserError,
} from "@/lib/vehicleLookupMessages"

type ApiVeiculoExtra = {
  chassi?: string
  combustivel?: string
  motor?: string
  potencia?: string | number
  uf_placa?: string
  quantidade_passageiro?: string | number
  capacidade_carga?: string | number
  situacao_veiculo?: string
  situacao_veiculo_descricao?: string
  municipio?: { municipio?: string }
  tipo_veiculo?: { tipo_veiculo?: string }
  cor_veiculo?: { cor?: string }
}

type ApiVeiculo = Record<string, unknown> & {
  placa?: string
  chassi?: string
  marca?: string
  MARCA?: string
  modelo?: string
  MODELO?: string
  versao?: string
  VERSAO?: string
  SUBMODELO?: string
  ano?: string | number
  ano_fabricacao?: string | number
  ano_modelo?: string | number
  anoModelo?: string | number
  cor?: string
  combustivel?: string
  potencia?: string | number
  quantidade_lugares?: string | number
  quantidade_passageiro?: string | number
  quantidade_portas?: string | number
  numero_motor?: string
  motor?: string
  tipo_veiculo?: string | { tipo_veiculo?: string }
  uf_jurisdicao?: string
  uf_placa?: string
  uf?: string
  logo?: string
  extra?: ApiVeiculoExtra
}

const ConsultaPlaca = () => {
  const [placa, setPlaca] = useState("")
  const [loading, setLoading] = useState(false)
  const [veiculo, setVeiculo] = useState<ApiVeiculo | null>(null)
  const [error, setError] = useState<VehicleLookupUserError | null>(null)
  const [cadastrando, setCadastrando] = useState(false)
  useIsMobile()
  const navigate = useNavigate()

  const consultarPlaca = async () => {
    if (!placa.trim()) {
      toast.error("Por favor, informe a placa do veículo")
      return
    }

    const placaNormalizada = normalizarPlaca(placa)
    if (!validarPlaca(placaNormalizada)) {
      setError(PLACA_INVALIDA_ERROR)
      setVeiculo(null)
      toast.error(PLACA_INVALIDA_ERROR.title)
      return
    }

    setLoading(true)
    setError(null)
    setVeiculo(null)

    try {
      const resultado = await consultarPlacaApiBrasil(placaNormalizada)
      if (!resultado.error && resultado.response) {
        setVeiculo(resultado.response)
        toast.success("Consulta realizada com sucesso!")
      } else {
        const userError = getPlateLookupUserError(resultado.message)
        setError(userError)
        toast.error(userError.title)
      }
    } catch {
      const userError = getPlateLookupUserError()
      setError(userError)
      toast.error(userError.title)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      consultarPlaca()
    }
  }

  // Normaliza campos que podem vir uppercase (formato antigo) ou lowercase (formato novo)
  const campo = (api: ApiVeiculo, novoNome: string, antigoNome?: string) =>
    String(api[novoNome] ?? (antigoNome ? api[antigoNome] : undefined) ?? '')

  const mapearCombustivel = (combustivel: string) => {
    if (!combustivel) return 'gasolina'
    const v = combustivel.toLowerCase().replace(/[\s\-_/\\]+/g, '').normalize('NFD').replace(/[̀-ͯ]/g, '')
    if (v.includes('alcoolgasolina') || v.includes('alcoolegasolina') || v.includes('alcoogasolina')) return 'alcool_gasolina'
    const map: Record<string, string> = {
      gasolina: 'gasolina', alcool: 'etanol', etanol: 'etanol', flex: 'flex',
      diesel: 'diesel', gnv: 'gnv', eletrico: 'eletrico', hibrido: 'hibrido'
    }
    return map[v] || 'gasolina'
  }

  const mapearDadosParaCadastro = (api: ApiVeiculo) => ({
    placa: typeof api.placa === 'string' ? api.placa : '',
    marca: campo(api, 'marca', 'MARCA'),
    modelo: campo(api, 'modelo', 'MODELO'),
    ano: parseInt(String(api.ano_fabricacao ?? api.ano ?? '')) || new Date().getFullYear(),
    cor: String(api.cor ?? api.extra?.cor_veiculo?.cor ?? 'branco').toLowerCase(),
    combustivel: mapearCombustivel(String(api.combustivel ?? api.extra?.combustivel ?? '')),
    cambio: 'manual',
    quilometragem: 0,
    motor: String(api.numero_motor ?? api.extra?.motor ?? api.motor ?? ''),
    portas: Number(api.quantidade_portas ?? 4),
    observacoes: `Veículo cadastrado via consulta de placa. Chassi: ${String(api.chassi ?? api.extra?.chassi ?? 'Não informado')}`
  })

  // Função para verificar se o veículo já existe
  const verificarVeiculoExistente = async (placa: string) => {
    try {
      const placaNormalizada = normalizarPlaca(placa)
      const { data, error } = await supabase
        .from('veiculos')
        .select('id')
        .eq('placa', placaNormalizada)
        .single()

      return !error && data !== null
    } catch {
      return false
    }
  }

  // Função para cadastrar o veículo (redirecionar para formulário)
  const cadastrarVeiculo = async () => {
    if (!veiculo) return

    setCadastrando(true)
    
    try {
      if (!veiculo.placa) {
        toast.error("Placa não encontrada no resultado da consulta")
        return
      }

      // Verificar se o veículo já existe
      const existe = await verificarVeiculoExistente(veiculo.placa)
      if (existe) {
        toast.error("Este veículo já está cadastrado no sistema")
        return
      }

      // Mapear dados para o formato do banco
      const dadosVeiculo = mapearDadosParaCadastro(veiculo)

      // Navegar para o formulário com os dados pré-preenchidos
      navigate("/novo-veiculo", { state: { dadosConsulta: dadosVeiculo } })
    } catch (error) {
      toast.error("Erro inesperado ao processar dados do veículo")
      console.error("Erro:", error)
    } finally {
      setCadastrando(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Consulta por Placa" 
        description="Consulte informações do veículo pela placa"
      />

      {/* Formulário de Consulta */}
      <Card>
        <CardHeader>
          <CardTitle>Consultar Veículo</CardTitle>
          <CardDescription>
            Informe a placa do veículo no formato AAA0A00 ou AAA0000
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <label htmlFor="placa" className="text-sm font-medium mb-2 block">
                Placa do Veículo
              </label>
              <div className="relative">
                <Car className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="placa"
                  placeholder="Ex: ABC1D23"
                  value={placa}
                  onChange={(e) => setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  onKeyPress={handleKeyPress}
                  className="h-11 pl-10 uppercase"
                  maxLength={7}
                  disabled={loading}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
              </div>
            </div>
            <Button 
              onClick={consultarPlaca} 
              disabled={loading}
              className="w-full md:w-auto"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Consultando...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Consultar
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultados */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{error.title}</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {veiculo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span>Resultado da Consulta</span>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle className="h-3 w-3 mr-1" />
                Encontrado
              </Badge>
            </CardTitle>
            <CardDescription>
              Dados do veículo consultado através da placa {placa}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end mb-4">
              <Button 
                onClick={cadastrarVeiculo} 
                disabled={cadastrando}
                className="w-full bg-green-600 hover:bg-green-700 sm:w-auto"
              >
                {cadastrando ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Cadastrar Veículo
                  </>
                )}
              </Button>
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Placa</p>
                <p className="font-semibold">{veiculo.placa}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Chassi</p>
                <p className="font-semibold">{veiculo.chassi || veiculo.extra?.chassi}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Marca</p>
                <p className="font-semibold">{veiculo.marca || veiculo.MARCA}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Modelo</p>
                <p className="font-semibold">{veiculo.modelo || veiculo.MODELO}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Versão</p>
                <p className="font-semibold">{veiculo.versao || veiculo.VERSAO || veiculo.SUBMODELO}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Ano de Fabricação</p>
                <p className="font-semibold">{veiculo.ano_fabricacao ?? veiculo.ano}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Ano do Modelo</p>
                <p className="font-semibold">{veiculo.ano_modelo ?? veiculo.anoModelo}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Cor</p>
                <p className="font-semibold">{veiculo.cor || veiculo.extra?.cor_veiculo?.cor}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Combustível</p>
                <p className="font-semibold">{veiculo.combustivel || veiculo.extra?.combustivel}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Potência</p>
                <p className="font-semibold">{veiculo.potencia || veiculo.extra?.potencia} cv</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Passageiros</p>
                <p className="font-semibold">
                  {veiculo.quantidade_lugares ?? veiculo.extra?.quantidade_passageiro ?? veiculo.quantidade_passageiro ?? '—'} lugares
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Tipo de Veículo</p>
                <p className="font-semibold">
                  {typeof veiculo.tipo_veiculo === 'string' ? veiculo.tipo_veiculo : veiculo.tipo_veiculo?.tipo_veiculo || veiculo.extra?.tipo_veiculo?.tipo_veiculo}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Estado</p>
                <p className="font-semibold">
                  {veiculo.uf_jurisdicao || veiculo.uf_placa || veiculo.uf || veiculo.extra?.uf_placa}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Número do Motor</p>
                <p className="font-semibold">{veiculo.numero_motor || veiculo.extra?.motor || veiculo.motor}</p>
              </div>
              {veiculo.logo && (
                <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                  <p className="text-sm font-medium text-muted-foreground">Logo</p>
                  <img src={veiculo.logo} alt={`Logo ${veiculo.marca || veiculo.MARCA}`} className="h-16 object-contain" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Informações sobre a consulta */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-sm">Sobre a Consulta</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Esta consulta fornece informações oficiais e detalhadas sobre veículos, incluindo marca, modelo, versão, ano de fabricação, cor, tipo de combustível, potência e outros dados relevantes para cadastro ou avaliação do veículo.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default ConsultaPlaca
